import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { addMinutes, compareAsc, differenceInMinutes, differenceInSeconds, format } from "date-fns";
import { ColumnProps, TimeCellComponent, EditorValidator, IventisColDef } from "@iventis/data-table";
import { Content } from "@iventis/translations/content/typed-content";
import { findNode } from "@iventis/tree-browser/tree-operations";
import { Node } from "@iventis/domain-model/model/node";
import { StartTimeType } from "@iventis/domain-model/model/startTimeType";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { ReplaySubject } from "rxjs";
import { utcToZonedTime } from "date-fns-tz";
import { DataField } from "@iventis/domain-model/model/dataField";
import { TextCellRenderer } from "@iventis/data-table/components/text-cell-renderer";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import {
    convertDataFieldsToColumns,
    disableFilteringOnColumn,
    generateBooleanColumn,
    generateListColumn,
    generateNumberColumn,
    generateTextColumn,
    generateTimeColumn,
    getTimeCellValue,
    generateTimeSpanColumn,
} from "@iventis/data-table/helpers/column-generators";
import { isValidDate, canEdit, spaceBetweenCapitalLetters } from "@iventis/utilities";
import { translate } from "@iventis/translations/translation";
import { TextCell } from "@iventis/data-table/components/data-table";
import { updateEventBlock, EventBlockPutResponse } from "./schedule-api-functions";
import {
    getSpecificDataFields,
    dayNumberFromDate,
    getEventBlockHierarchySystemDataFieldValues,
    findInheritedDataFieldValue,
    findTopCalendarlevelEventBlock,
} from "./schedule-helper-functions";
import { ScheduleMachineContext } from "./schedule.machine.types";

import { getDisciplineDataField, SportEventDataFieldNames } from "./datafields";
import { ScheduleTableRowNode } from "./schedule-table.types";
import {
    findHierarchyEntityById,
    findAncestorInHierarchy,
    findEventBlockHierarchyEntitiesByCalendarLevel,
    findHierarchyEntitiesByType,
    hierarchyToArray,
} from "./hierarchy.helpers";
import { DurationCellEditor, ScheduleTableDurationCellEditor, ScheduleTableTimeCellEditor } from "./schedule-table.components";
import { getVenueLocationTypeIdByEventBlockCalendarLevel } from "./event-block-helpers";

export const scheduleTableTimeValidator = (): EditorValidator => (node, colId, value) => {
    const { startAt, endAt } = node.data;
    let newEndAt = endAt?.value;
    let newStartAt = startAt?.value;
    switch (colId) {
        case "duration": {
            /* eslint-disable @typescript-eslint/no-explicit-any  */
            newEndAt = addMinutes(startAt.value, value as any);
            break;
        }
        case "endAt": {
            newEndAt = (value as any)?.value;
            break;
        }
        case "startAt": {
            newStartAt = (value as any)?.value;
            /* eslint-enable @typescript-eslint/no-explicit-any  */
            break;
        }
        default:
            break;
    }
    if (differenceInSeconds(newEndAt, newStartAt) < 0) {
        return { doesFail: true, errorMessage: translate(Content.schedule2.errorMessages.must_end_after_start_time) };
    }

    return { doesFail: false };
};

/**
 *
 * @param {ScheduleMachineContext} machineContext - schedule machine context
 * @param translate - useIventisTranslate function
 * @returns {ColumnProps[]} - column props to pass into data table
 */
export const generateColumnsForScheduleTable = (
    { schedule, venueLocations, eventBlockHierarchy }: ScheduleMachineContext,
    translate: UseIventisTranslate,
    showEventUnits: boolean,
    getListItems: (id: string) => ReplaySubject<DataFieldListItem[]>
): ColumnProps[] => [
    {
        agGridColumnProps: {
            headerName: translate(Content.table.schedule.day),
            field: "day",
            suppressMenu: true,
            sortable: true,
            cellRenderer: TextCell,
        },
    },
    {
        agGridColumnProps: {
            headerName: translate(Content.table.schedule.date),
            field: "date",
            suppressMenu: true,
            sortable: true,
            comparator: (valueA, valueB) => compareAsc(valueA.value, valueB.value),
            cellRenderer: TimeCellComponent,
        },
    },
    generateTimeColumn({ headerName: translate(Content.table.schedule.start), field: "startAt" }, getTimeCellValue, {
        cellEditor: ScheduleTableTimeCellEditor,
        cellEditorParams: { validator: scheduleTableTimeValidator(), getTimeCellValue },
    }),
    generateDurationColumn({ cellEditor: ScheduleTableDurationCellEditor }),
    generateTimeColumn({ headerName: translate(Content.table.schedule.end), field: "endAt" }, getTimeCellValue, {
        cellEditor: ScheduleTableTimeCellEditor,
        cellEditorParams: { validator: scheduleTableTimeValidator(), getTimeCellValue },
    }),
    ...(!showEventUnits ? [generateTimeSpanColumn({ headerName: translate(Content.table.schedule.zero), field: "zeroAt" }, getTimeCellValue)] : []),
    generateListColumn(
        {
            componentParams: {
                listItems: findHierarchyEntitiesByType(
                    venueLocations,
                    getVenueLocationTypeIdByEventBlockCalendarLevel(eventBlockHierarchy.children, showEventUnits ? 2 : 1),
                    (venueLocation: VenueLocation) => venueLocation.type.id
                ).map(({ item }) => item),
            },
            filterOptions: (options, node: ScheduleTableRowNode) => {
                const parent = findAncestorInHierarchy(eventBlockHierarchy, node.data.id, 2);
                return parent ? findHierarchyEntityById(venueLocations, parent.item.venueLocation.id).children.map(({ item }) => item) : options;
            },
        },
        { headerName: showEventUnits ? translate(Content.table.schedule.space) : translate(Content.table.schedule.venue), field: "venueLocation" }
    ),
    {
        agGridColumnProps: {
            headerName: translate(Content.table.schedule.description),
            field: "description",
            suppressMenu: true,
            sortable: true,
            cellRenderer: TextCell,
            cellClass: "truncate-cell-contents",
            cellClassRules: {
                "truncate-cell-contents": () => true,
            },
            width: 200,
        },
    },
    generateTextColumn({ field: getDisciplineDataField(schedule.config.dataFields).id, headerName: translate(SportEventDataFieldNames.DISCIPLINE) }, { editable: false }),
    generateBooleanColumn({ field: "estimatedTime", headerName: "Estimated Time" }),
    // Pass callback through here
    ...convertDataFieldsToColumns(getSpecificDataFields(schedule.config, showEventUnits), {
        listItemGetter: getListItems,
        filterListItems: filterDataFieldListItems(schedule.config.dataFields, eventBlockHierarchy),
    }),
    ...(showEventUnits
        ? [
              generateTextColumn({ field: EventBlockSystemDataFieldType.Code, headerName: translate(Content.schedule2.data_fields.session_code) }, { editable: false }),
              generateListColumn(
                  {
                      componentParams: {
                          listItems: Object.values(StartTimeType).map((StartTimeTypeValue) => ({ id: StartTimeTypeValue, name: spaceBetweenCapitalLetters(StartTimeTypeValue) })),
                      },
                  },
                  { field: "startTimeType", headerName: "Start Time Type" },
                  { editable: false }
              ),
          ]
        : []),
];

/** Filter data field list item options when editing a cell  */
const filterDataFieldListItems = (allDataFields: DataField[], eventBlockHierarchy: Hierarchy<EventBlock>) => (
    items: DataFieldListItem[],
    node: ScheduleTableRowNode,
    colId: string
): DataFieldListItem[] => {
    const dataField = allDataFields.find((df) => df.id === colId);
    const parentDataField = allDataFields.find((df) => df.id === dataField.parentDataFieldId);
    const eventBlock = findHierarchyEntityById(eventBlockHierarchy.children, node.data.id);
    return items.filter((item) => {
        if (parentDataField) {
            const parentDataFieldValue = findInheritedDataFieldValue(eventBlock, parentDataField?.id, eventBlockHierarchy);
            return parentDataFieldValue?.metaData?.[parentDataField.customerIdentifierName] === item.metaData?.[dataField.hierarchyMetadataPropertyName];
        }
        return true;
    });
};

/**
 *
 * @param {ScheduleMachineContext} context - schedule machine context
 * @returns Row data
 */
export const generateRowsForScheduleTable = (context: ScheduleMachineContext, showEventUnits: boolean, timezone: string): ScheduleTableRowNode["data"][] => {
    const { eventBlockHierarchy, availableDays, selectedScheduleVersionHierarchy } = context;

    return (showEventUnits
        ? findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy.children, 2)
        : findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy.children, 1)
    ).map(({ item }) => {
        const { id, startAt, endAt, type, venueLocation, dataFieldValues, scheduleVersionId, zeroAt, startTimeType, estimatedTime, relativeEventRules } = item;

        const scheduleVersion = selectedScheduleVersionHierarchy.children.find((v) => v.item.id === item.scheduleVersionId)?.item as ScheduleVersion;
        const disciplineDataFieldValue = scheduleVersion.dataFieldValues.find((dfv) => dfv.dataField.name === SportEventDataFieldNames.DISCIPLINE);

        const dataFieldValuesAsObject = dataFieldValues.reduce((cum, { dataField, value }) => ({ ...cum, [dataField.id]: value }), {});

        const scheduleVersionNode = findNode(context.scheduleNode.childNodes, scheduleVersionId) as Node;
        const scheduleVersionPermission = scheduleVersionNode?.authorisation;

        const eventDay = dayNumberFromDate(availableDays, startAt);

        let eventUnitHierarchyValues: { [key: string]: string };

        if (showEventUnits) {
            const eventBlockHierarchyDisplayValues = getEventBlockHierarchySystemDataFieldValues<string | { name: string }, string>(
                eventBlockHierarchy.children,
                item,
                [EventBlockSystemDataFieldType.Code],
                (value) => (typeof value === "string" ? value : value.name)
            );

            eventUnitHierarchyValues = {
                [EventBlockSystemDataFieldType.Code]: eventBlockHierarchyDisplayValues[EventBlockSystemDataFieldType.Code],
            };
        }

        return {
            id,
            zeroAt,
            startAt: { displayText: format(utcToZonedTime(startAt, timezone), "HH:mm:ss"), value: startAt },
            endAt: { displayText: format(utcToZonedTime(endAt, timezone), "HH:mm:ss"), value: endAt },
            type,
            venueLocation,
            day: eventDay,
            date: { displayText: format(utcToZonedTime(startAt, timezone), "dd/MM/yyyy"), value: availableDays.get(eventDay) },
            canEdit: canEdit(scheduleVersionPermission),
            [disciplineDataFieldValue.dataField.id]: disciplineDataFieldValue.value.name,
            description: item.description ?? "",
            startTimeType,
            estimatedTime,
            relativeEventRules,
            ...eventUnitHierarchyValues,
            ...dataFieldValuesAsObject,
        };
    });
};

/**
 * Converts the editRow params for a single cell into an async call to update an EventBlock
 * @returns {Promise<EventBlock>} - returns a promise for the resulting updated event block
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const editSingleCell = async (rowNode: ScheduleTableRowNode, columnId: string, machineContext: ScheduleMachineContext, newValue: any): Promise<EventBlockPutResponse> => {
    const eventBlock = findHierarchyEntityById([machineContext.eventBlockHierarchy], rowNode.data.id) as Hierarchy<EventBlock>;
    const oldEventBlockItem = JSON.parse(JSON.stringify(eventBlock.item));
    switch (columnId as keyof ScheduleTableRowNode["data"]) {
        case "endAt": {
            eventBlock.item[columnId] = newValue.value;
            break;
        }
        case "startAt": {
            eventBlock.item[columnId] = newValue.value;
            break;
        }
        case "zeroAt": {
            eventBlock.item[columnId] = newValue;
            break;
        }
        case "venueLocation": {
            const venueLocations = machineContext.venueLocations.flatMap((venueLocationHierarchy) => hierarchyToArray(venueLocationHierarchy));
            const venueLocation = venueLocations.find((venueLocation) => venueLocation.id === newValue.id);
            eventBlock.item[columnId] = venueLocation;
            break;
        }
        default:
            // eslint-disable-next-line no-lone-blocks
            {
                if (machineContext.schedule.config.eventBlockTypes.find((type) => rowNode.data.type.id === type.id).dataFields.some((df) => df.dataFieldId === columnId)) {
                    const dataField = machineContext.schedule.config.dataFields.find((df) => df.id === columnId);
                    const dataFieldValueIndex = eventBlock.item.dataFieldValues.findIndex((dfv) => dfv.dataField.id === columnId);
                    if (dataFieldValueIndex !== -1) {
                        eventBlock.item.dataFieldValues[dataFieldValueIndex] = {
                            dataField: eventBlock.item.dataFieldValues[dataFieldValueIndex].dataField,
                            value: newValue,
                        };
                    } else if (dataField != null || dataField.name !== SportEventDataFieldNames.DISCIPLINE) {
                        eventBlock.item.dataFieldValues.push({ dataField, value: newValue });
                    }
                }
            }
            break;
    }

    const session = findTopCalendarlevelEventBlock(eventBlock, [machineContext.eventBlockHierarchy]);
    const response = await updateEventBlock(oldEventBlockItem, eventBlock, session.item.id);
    return response;
};

export function convertEventBlockTypesToColumn(eventBlockTypes: ScheduleConfigEventBlockType[]): ColumnProps[] {
    return eventBlockTypes.map((type) => {
        const fieldDetails = {
            field: type.id,
            headerName: type.name,
        };
        return generateTextColumn(fieldDetails, { editable: false });
    });
}

export const generateDurationColumn = (columnPropsOverrides?: IventisColDef) =>
    generateNumberColumn(
        { field: "duration", headerName: translate(Content.runsheet.headings.duration) },
        {
            editable: true,
            valueGetter: ({ data }) =>
                isValidDate(data.endAt.value) && !data.id?.includes("ZERO") ? differenceInMinutes(new Date(data.endAt?.value), new Date(data.startAt?.value)) : "",
            cellEditor: DurationCellEditor,
            cellRenderer: TextCellRenderer,
            cellEditorParams: { validator: scheduleTableTimeValidator() },
            ...disableFilteringOnColumn(),
            ...columnPropsOverrides,
        }
    );
